import React from 'react';
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';

import { ButtonV3, InputV2 } from 'components/ComponentV2';
import { Button, Form } from 'components/form';
import { Col, Row } from 'components/layout';

import { API_ROUTES } from 'lib/api-routes';
import { ONBOARDING_FLOW_STATE } from 'lib/consts/flowState';
import { TUserApiResponse } from 'lib/models/User.model';
import { useAuth } from 'lib/providers/AuthProvider';
import { apiInstance } from 'lib/utils/axios';
import { parseErrorResponse } from 'lib/utils/parser';
import { REGEX } from 'lib/utils/regex';

import Footer from '../Footer';
import Header from '../Header';
import StepHeading from '../StepHeading';
import styles from './Step5.module.scss';

interface Step5Props {
  progress: number;
  setStep: (step: number) => void;
}

interface Inputs {
  career_page_link: string;
}

export const Step5: React.FC<Step5Props> = ({ progress, setStep }) => {
  const { updateUserApiResponse, recruiterCompany } = useAuth();

  const [t] = useTranslation('signup.recruiter');

  const [addCareerPageLink, setAddCareerPageLink] = React.useState<boolean>(
    !!recruiterCompany?.career_page_link
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      career_page_link: recruiterCompany?.career_page_link,
    },
  });

  const [showError, setShowError] = React.useState<string>('');

  const setPreOnboardingCompleted = async () => {
    const recruiter = { flow_state: ONBOARDING_FLOW_STATE.PRE_COMPLETE };
    const { data: response } = await apiInstance.patch<TUserApiResponse>(
      API_ROUTES.RECRUITER_SIGNUP,
      {
        recruiter,
      }
    );

    updateUserApiResponse(response);
    setStep(6);
  };

  const enablePreAutoAddHandler = async () => {
    const body = {
      company: {
        pre_auto_add: true,
      },
    };
    try {
      await apiInstance.put(API_ROUTES.COMPANY_PROFILE, body);
    } catch (error) {
      console.error(error);
    }
  };

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    const body = {
      company: { ...data },
    };
    try {
      await apiInstance.patch(API_ROUTES.COMPANY_PROFILE, body);
      setPreOnboardingCompleted();
    } catch (error) {
      const message = parseErrorResponse(error);
      setShowError(message);
    }
  };

  return (
    <>
      <Header
        percentComplete={progress}
        onBack={() => {
          setStep(4);
        }}
      />
      <div className={styles.stepContent}>
        <StepHeading title={t('step-5.header.title')} />

        <Form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <Button
            type="button"
            color="secondary"
            className={styles.yesNoButton}
            onClick={setPreOnboardingCompleted}
            isFullWidth
          >
            {t('no')}
          </Button>
          <Button
            type="button"
            color="secondary"
            className={styles.yesNoButton}
            onClick={() => {
              setAddCareerPageLink(true);
              enablePreAutoAddHandler();
            }}
            isFullWidth
            disabled={addCareerPageLink}
          >
            {t('yes')}
          </Button>

          {addCareerPageLink && (
            <Row className={styles.mb24}>
              <Col span={24}>
                <p className={styles.careerPageLinkDescription}>
                  {t('step-5.career-link.desc')}
                </p>
                <InputV2
                  label={t('step-5.career-link.label')}
                  {...register('career_page_link', {
                    required: t('step-5.career-link.required'),
                    pattern: {
                      value: REGEX.URL,
                      message: t('step-5.career-link.invalid'),
                    },
                  })}
                  placeholder={t('step-5.career-link.placeholder')}
                  labelClassName={styles.fieldLabel}
                  isInvalid={!!errors.career_page_link?.message}
                />
                {errors.career_page_link?.message && (
                  <div className={styles.errorMessage}>
                    {errors.career_page_link?.message}
                  </div>
                )}
              </Col>
            </Row>
          )}

          {showError && <div className={styles.errorMessage}>{showError}</div>}
          {addCareerPageLink && (
            <Footer>
              <ButtonV3
                type="submit"
                isFullWidth
                isLoading={isSubmitting}
                size="xlarge"
              >
                {t('next')}
              </ButtonV3>
            </Footer>
          )}
        </Form>
      </div>
    </>
  );
};
